import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <>
      <SEO 
        title="Terms & Conditions"
        description="Terms and Conditions for Stephanies Luxe Solutions. Review our service terms, payment policies, and legal agreements for UAE short-term rental consultancy services."
        canonical="https://stephaniesluxesolutions.com/terms"
      />
      <style>{`
        :root {
          --espresso: #2C1F14;
          --espresso-deep: #221508;
          --espresso-mid: #4A3728;
          --mocha: #6B5744;
          --warm-gold: #C4A882;
          --gold-light: #D4BCA0;
          --cream: #F5F0E8;
          --sand: #EDE5D8;
          --warm-stone: #DDD4C0;
          --stone-mid: #C8BEA8;
        }

        body {
          font-family: 'DM Sans', sans-serif;
          background: var(--cream);
          color: var(--espresso);
        }

        .terms-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 80px 24px;
        }

        .terms-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .terms-back {
          display: inline-block;
          font-size: 10px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--mocha);
          text-decoration: none;
          border-bottom: 0.5px solid var(--warm-gold);
          padding-bottom: 2px;
          margin-bottom: 32px;
          transition: color 0.2s;
        }

        .terms-back:hover {
          color: var(--espresso);
        }

        .terms-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 42px;
          font-weight: 300;
          color: var(--espresso);
          margin-bottom: 12px;
          line-height: 1.2;
        }

        .terms-subtitle {
          font-size: 14px;
          color: var(--mocha);
          font-weight: 300;
        }

        .terms-section {
          margin-bottom: 48px;
        }

        .terms-section-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 24px;
          font-weight: 400;
          color: var(--espresso);
          margin-bottom: 16px;
        }

        .terms-text {
          font-size: 14px;
          line-height: 1.8;
          color: var(--mocha);
          margin-bottom: 16px;
          font-weight: 300;
        }

        .terms-list {
          list-style: none;
          padding-left: 0;
          margin: 16px 0;
        }

        .terms-list li {
          font-size: 14px;
          line-height: 1.8;
          color: var(--mocha);
          margin-bottom: 12px;
          padding-left: 24px;
          position: relative;
          font-weight: 300;
        }

        .terms-list li::before {
          content: "•";
          position: absolute;
          left: 8px;
          color: var(--warm-gold);
        }

        .terms-strong {
          font-weight: 500;
          color: var(--espresso);
        }

        .terms-divider {
          width: 32px;
          height: 0.5px;
          background: var(--warm-gold);
          margin: 60px auto;
        }

        @media (max-width: 768px) {
          .terms-container {
            padding: 60px 20px;
          }

          .terms-title {
            font-size: 32px;
          }

          .terms-section-title {
            font-size: 20px;
          }
        }
      `}</style>

      <div className="terms-container">
        <div className="terms-header">
          <Link to="/" className="terms-back">← Back to Home</Link>
          <h1 className="terms-title">Terms & Conditions</h1>
          <p className="terms-subtitle">Last updated: December 2025</p>
        </div>

        <section className="terms-section">
          <h2 className="terms-section-title">1. Agreement to Terms</h2>
          <p className="terms-text">
            By accessing and using the services of Stephanies Luxe Solutions FZ LLC (Trade License 46002090), you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, please do not use our services.
          </p>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">2. Services Provided</h2>
          <p className="terms-text">
            Stephanies Luxe Solutions provides consultancy services for short-term rental properties in the UAE, including but not limited to:
          </p>
          <ul className="terms-list">
            <li>Mystery shopping and property audits</li>
            <li>Listing and pricing management</li>
            <li>STR performance audits</li>
            <li>Holiday home business strategy</li>
            <li>Guest experience design</li>
            <li>Operational optimisation</li>
            <li>Vendor and STR apartment referrals</li>
          </ul>
          <p className="terms-text">
            All services are delivered on a professional consultancy basis. Implementation of recommendations remains the client's responsibility unless otherwise agreed in writing.
          </p>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">3. Client Responsibilities</h2>
          <p className="terms-text">Clients agree to:</p>
          <ul className="terms-list">
            <li>Provide accurate and complete information necessary for service delivery</li>
            <li>Grant access to properties, platforms, and data as required for audits and mystery shopping services</li>
            <li>Communicate any changes to property operations that may affect our recommendations</li>
            <li>Maintain confidentiality of proprietary methodologies and reports provided</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">4. Payment Terms</h2>
          <p className="terms-text">
            <span className="terms-strong">Pricing:</span> Service fees are confirmed in writing prior to engagement. Pricing may vary based on property size, scope of work, and complexity.
          </p>
          <p className="terms-text">
            <span className="terms-strong">Payment schedule:</span> Payment terms are specified in individual service agreements. Typically, a 50% deposit is required before work commences, with the balance due upon delivery of final reports or completion of services.
          </p>
          <p className="terms-text">
            <span className="terms-strong">Cancellation:</span> Cancellations made less than 48 hours before a scheduled mystery shopping visit or consultation will incur a 50% cancellation fee.
          </p>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">5. Confidentiality</h2>
          <p className="terms-text">
            All client information, property data, and business details are treated with strict confidentiality. We do not share, sell, or disclose client information to third parties except where required by UAE law or with explicit client consent.
          </p>
          <p className="terms-text">
            Mystery shopping reports and audit findings are provided exclusively to the client and are not published or shared publicly without written permission.
          </p>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">6. Limitation of Liability</h2>
          <p className="terms-text">
            Stephanies Luxe Solutions provides expert consultancy based on industry best practices and market data. However, we do not guarantee specific business outcomes such as occupancy rates, revenue increases, or review scores.
          </p>
          <p className="terms-text">
            Our liability is limited to the fees paid for the specific service in question. We are not liable for indirect, consequential, or incidental damages arising from our services or recommendations.
          </p>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">7. Intellectual Property</h2>
          <p className="terms-text">
            All methodologies, templates, frameworks, and proprietary tools used by Stephanies Luxe Solutions remain our intellectual property. Clients receive a license to use deliverables (such as audit reports and strategy documents) for their own operations but may not resell, redistribute, or claim ownership of our methodologies.
          </p>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">8. Mystery Shopping Services</h2>
          <p className="terms-text">
            Mystery shopping services involve genuine bookings made without advance notice to property managers or staff. Clients agree to accept mystery shoppers as regular guests and understand that the booking will be conducted anonymously to ensure authentic assessment.
          </p>
          <p className="terms-text">
            Costs associated with the mystery shopping stay (accommodation, cleaning fees, platform fees) are billed separately and agreed upon in advance.
          </p>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">9. Governing Law</h2>
          <p className="terms-text">
            These Terms and Conditions are governed by the laws of the United Arab Emirates. Any disputes arising from these terms or our services will be subject to the exclusive jurisdiction of the courts of Dubai, UAE.
          </p>
        </section>

        <section className="terms-section">
          <h2 className="terms-section-title">10. Changes to Terms</h2>
          <p className="terms-text">
            We reserve the right to update these Terms and Conditions at any time. Changes will be posted on this page with an updated "Last updated" date. Continued use of our services after changes constitutes acceptance of the revised terms.
          </p>
        </section>

        <div className="terms-divider"></div>

        <section className="terms-section">
          <h2 className="terms-section-title">Contact Information</h2>
          <p className="terms-text">
            For questions regarding these Terms and Conditions, please contact:<br/>
            <span className="terms-strong">Stephanies Luxe Solutions FZ LLC</span><br/>
            Trade License: 46002090<br/>
            Email: stephanieluxesolutions@gmail.com
          </p>
        </section>
      </div>
    </>
  );
};

export default Terms;
